import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { Eye, FileText, CheckCircle, Trash2, AlertTriangle, XCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import VATDetailView from '../VatForm';
import VatVerificationViewModal from '../VatVerificationViewModal';

const ReviewStep = ({
    vatPeriod,
    handleReviewVatPeriod,
    reviewVerification,
    handleDeleteVerification,
    operationInProgress,
    isPreviewMode
}) => {
    const { companyId } = useParams();
    const [validation, setValidation] = useState(null);
    const [mappingValidation, setMappingValidation] = useState(null);
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    // Re-fetch validation when vatPeriod changes OR when verification is created/deleted
    // The zeroing verification moves amounts off VAT accounts, changing the box values
    const fetchValidation = useCallback(() => {
        if (!vatPeriod) return;
        axios.get(getApiUrl(`${companyId}/reporting/vat-validation`), {
            params: { start_date: vatPeriod.start_date, end_date: vatPeriod.end_date, fiscal_year_id: vatPeriod.fiscal_year_id },
            withCredentials: true
        }).then(res => {
            if (res.data.success) setValidation(res.data.validation);
        }).catch(err => console.error('Validation fetch error:', err));

        axios.get(getApiUrl(`${companyId}/reporting/vat-mapping-validation`), {
            withCredentials: true
        }).then(res => {
            if (res.data.success) setMappingValidation(res.data.validation);
        }).catch(err => console.error('Mapping validation fetch error:', err));
    }, [companyId, vatPeriod]);

    useEffect(() => {
        fetchValidation();
    }, [fetchValidation, reviewVerification]);

    return (
        <div className="step-container">
            <style>{`
                .verification-status-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                }
                .verification-status-card .btn-sm {
                    padding: 0.375rem 0.75rem;
                    font-size: 0.875rem;
                }
            `}</style>

            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#3b82f6',
                            color: 'white'
                        }}
                    >
                        <Eye size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Granska Momsrapporten</h3>
                        <p className="text-muted mb-0">Kontrollera att uppgifterna är korrekta innan du fortsätter</p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                {/* Only show validation warnings BEFORE the verification is created */}
                {/* After booking, the VAT accounts are zeroed so the warnings become misleading */}
                {!reviewVerification && (
                    <>
                        {/* VAT Mapping Setup Warnings */}
                        {mappingValidation && (mappingValidation.errors.length > 0 || mappingValidation.warnings.length > 0) && (
                            <div className="mb-3">
                                {mappingValidation.errors.map((err, i) => (
                                    <Alert key={`me-${i}`} variant="danger" className="d-flex align-items-start">
                                        <XCircle size={18} className="me-2 mt-1 flex-shrink-0" />
                                        <div>{err.message}</div>
                                    </Alert>
                                ))}
                                {mappingValidation.warnings.map((warn, i) => (
                                    <Alert key={`mw-${i}`} variant="warning" className="d-flex align-items-start">
                                        <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                        <div>{warn.message}</div>
                                    </Alert>
                                ))}
                            </div>
                        )}

                        {/* VAT Report Validation */}
                        {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
                            <div className="mb-3">
                                <h6>Momskontroll</h6>
                                {validation.errors.map((err, i) => (
                                    <Alert key={`ve-${i}`} variant="danger" className="d-flex align-items-start">
                                        <XCircle size={18} className="me-2 mt-1 flex-shrink-0" />
                                        <div>{err.message}</div>
                                    </Alert>
                                ))}
                                {validation.warnings.map((warn, i) => (
                                    <Alert key={`vw-${i}`} variant="warning" className="d-flex align-items-start">
                                        <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                <div>{warn.message}</div>
                            </Alert>
                        ))}
                    </div>
                )}
                    </>
                )}

                {vatPeriod ? (
                    <div className="mb-4">
                        <VATDetailView period={vatPeriod} fiscalYearId={vatPeriod.fiscal_year_id} />
                    </div>
                ) : (
                    <div className="d-flex align-items-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Laddar momsuppgifter...</span>
                    </div>
                )}
            </div>

            <div className="step-actions">
                {reviewVerification ? (
                    <div className="d-flex align-items-center justify-content-between">
                        <button
                            type="button"
                            onClick={() => setShowVerificationModal(true)}
                            className="d-inline-flex align-items-center gap-2 border-0 bg-transparent p-0"
                            style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                        >
                            <CheckCircle size={16} className="text-success" />
                            <span style={{ color: '#166534', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                Verifikat {reviewVerification.series}{reviewVerification.number}
                            </span>
                        </button>
                        {vatPeriod?.state === 'open' && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={() => handleDeleteVerification(reviewVerification.verification_id, 'review')}
                                disabled={operationInProgress || isPreviewMode}
                                style={{ fontSize: '0.8rem' }}
                            >
                                <Trash2 size={13} className="me-1" />
                                Ångra
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="d-flex justify-content-center">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleReviewVatPeriod}
                                className="d-flex align-items-center px-4"
                                disabled={operationInProgress || isPreviewMode}
                            >
                                <FileText size={20} className="me-2" />
                                Skapa bokföringsförslag för momsrapport
                            </Button>
                        </div>
                        <div className="text-center mt-3 text-muted small">
                            Momskontona nollställs och saldot flyttas till redovisningskontot (2650).
                        </div>
                    </>
                )}
            </div>

            {reviewVerification && (
                <VatVerificationViewModal
                    show={showVerificationModal}
                    onHide={() => setShowVerificationModal(false)}
                    verificationId={reviewVerification.verification_id}
                />
            )}
        </div>
    );
};

export default ReviewStep;